import Grid from '@mui/material/Grid';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format } from 'date-fns';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RHFDatePickerField } from '../../DatePickerField/RHFDatePickerField.tsx';
import { datePickerLocale } from '../../utils/datePickerLocale.ts';
import { dateRangeFormFields } from '../constants.ts';

export type DateRangeInputsProps = {
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  dateFormat: string;
  endDateOptional?: boolean;
  startDateOptional?: boolean;
  loading?: boolean;
};

export const DateRangeInputs = ({
  disabled,
  minDate,
  maxDate,
  dateFormat,
  endDateOptional = false,
  startDateOptional = false,
  loading = false,
}: DateRangeInputsProps) => {
  const { t } = useTranslation();
  const { watch, setValue, trigger } = useFormContext();

  const observableStartDate = watch(dateRangeFormFields.START_DATE);
  const observableEndDate = watch(dateRangeFormFields.END_DATE);

  const handleDateChange = useCallback(
    (value: Date | null, field: string) => {
      setValue(field, value, { shouldDirty: true, shouldValidate: true });

      if (field === dateRangeFormFields.START_DATE) {
        trigger(dateRangeFormFields.END_DATE);
      } else if (field === dateRangeFormFields.END_DATE) {
        trigger(dateRangeFormFields.START_DATE);
      }
    },
    [setValue, trigger]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={datePickerLocale}>
      <Grid container spacing={1}>
        <Grid size={6}>
          <RHFDatePickerField
            disabled={disabled}
            loading={loading}
            name={dateRangeFormFields.START_DATE}
            label={`${t('Start date')}${startDateOptional ? ` (${t('optional')})` : ''}`}
            data-test={dateRangeFormFields.START_DATE}
            maxDate={observableEndDate ?? maxDate}
            placeholder={minDate && format(minDate, dateFormat)}
            minDate={minDate}
            onChange={value => handleDateChange(value, dateRangeFormFields.START_DATE)}
            clearable={startDateOptional}
          />
        </Grid>

        <Grid size={6}>
          <RHFDatePickerField
            disabled={disabled}
            loading={loading}
            name={dateRangeFormFields.END_DATE}
            label={`${t('End date')}${endDateOptional ? ` (${t('optional')})` : ''}`}
            data-test={dateRangeFormFields.END_DATE}
            maxDate={maxDate}
            minDate={observableStartDate ?? minDate}
            placeholder={maxDate && format(maxDate, dateFormat)}
            clearable={endDateOptional}
            onChange={value => handleDateChange(value, dateRangeFormFields.END_DATE)}
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};
